import { useYardStores } from '@yard/AppProvider'
import { isRoadConnectableLayoutObject } from '@yard/utils/road-management.util'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef } from 'react'
import { Node } from 'reactflow'
import { GATE_ROAD_NODE_MARGIN } from '../helpers/yard-view.utils'
import { RoadNodeWithActions, YardBlockWithOccupanciesAndActions } from '../models/yard-view.model'
import { RoadNetworkViewStore } from '../stores/nodes/road-network.view-store'

interface NodeDragState {
  nodePreDragPos: { x: number; y: number }
  roadNodePreDragPositions: Map<string, { x: number; y: number }>
}

interface UseRoadNodeDragParams {
  roadNetworkViewStore: RoadNetworkViewStore
  nodes: Node[]
  setNodes: Dispatch<SetStateAction<Node[]>>
}

interface UseRoadNodeDragResult {
  onNodeDragStart: (event: React.MouseEvent, node: Node) => void
  onNodeDrag: (event: React.MouseEvent, node: Node) => void
  handleRoadNodeDragStop: (node: Node) => boolean
}

export const useRoadNodeDrag = ({
  roadNetworkViewStore,
  nodes,
  setNodes,
}: UseRoadNodeDragParams): UseRoadNodeDragResult => {
  const nodesRef = useRef(nodes)
  const dragState = useRef<Map<string, NodeDragState>>(new Map())
  const { layoutObjectStore } = useYardStores()

  useEffect(() => {
    nodesRef.current = nodes
  }, [nodes])

  const clampRoadNodeToGate = (
    position: { x: number; y: number },
    layoutObjectId: string,
  ): { x: number; y: number } => {
    const gateNode = nodesRef.current.find(n => n.id === layoutObjectId)
    const gate = layoutObjectStore.layoutObjects[layoutObjectId]

    if (!gateNode || !gate) return position

    return {
      x: Math.max(
        gateNode.position.x - GATE_ROAD_NODE_MARGIN,
        Math.min(gateNode.position.x + gate.width + GATE_ROAD_NODE_MARGIN, position.x),
      ),
      y: Math.max(
        gateNode.position.y - GATE_ROAD_NODE_MARGIN,
        Math.min(gateNode.position.y + gate.height + GATE_ROAD_NODE_MARGIN, position.y),
      ),
    }
  }

  const onNodeDragStart = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const isLayoutObjectWithRoadNodes = isRoadConnectableLayoutObject(node)

      if (!isLayoutObjectWithRoadNodes && node.type !== 'yardBlock') return

      const roadNodePreDragPositions = new Map<string, { x: number; y: number }>()

      if (isLayoutObjectWithRoadNodes) {
        roadNetworkViewStore.nodes
          .filter(n => n.layoutObjectId === node.id)
          .forEach(roadNode => {
            const rfNode = nodesRef.current.find(n => n.id === `road-node-${roadNode.id}`)
            if (rfNode) roadNodePreDragPositions.set(roadNode.id, { ...rfNode.position })
          })
      } else if (node.type === 'yardBlock') {
        const blockData = node.data as YardBlockWithOccupanciesAndActions
        const accessPointIds = new Set((blockData.accessPoints ?? []).map(ap => ap.id))

        roadNetworkViewStore.nodes
          .filter(n => n.blockAccessPointId && accessPointIds.has(n.blockAccessPointId))
          .forEach(roadNode => {
            const rfNode = nodesRef.current.find(n => n.id === `road-node-${roadNode.id}`)
            if (rfNode) roadNodePreDragPositions.set(roadNode.id, { ...rfNode.position })
          })
      }

      dragState.current.set(node.id, {
        nodePreDragPos: { ...node.position },
        roadNodePreDragPositions,
      })
    },
    [roadNetworkViewStore],
  )

  const onNodeDrag = useCallback(
    (_: React.MouseEvent, node: Node) => {
      if (node.type === 'roadNode') {
        const roadData = node.data as RoadNodeWithActions

        // Access-point-linked nodes are fixed at their placed position.
        if (roadData.blockAccessPointId) {
          const persisted = roadNetworkViewStore.nodes.find(n => n.id === roadData.id)

          if (persisted) {
            const fixed = {
              x: persisted.layoutPosition.x,
              y: persisted.layoutPosition.y,
            }

            if (fixed.x !== node.position.x || fixed.y !== node.position.y) {
              setNodes(prev => prev.map(n => (n.id === node.id ? { ...n, position: fixed } : n)))
            }
          }
          return
        }

        if (!roadData.layoutObjectId) return

        const clamped = clampRoadNodeToGate(node.position, roadData.layoutObjectId)

        if (clamped.x !== node.position.x || clamped.y !== node.position.y) {
          setNodes(prev => prev.map(n => (n.id === node.id ? { ...n, position: clamped } : n)))
        }

        return
      }

      const isDragParent = isRoadConnectableLayoutObject(node) || node.type === 'yardBlock'

      if (isDragParent) {
        const state = dragState.current.get(node.id)

        if (!state) return

        const delta = {
          x: node.position.x - state.nodePreDragPos.x,
          y: node.position.y - state.nodePreDragPos.y,
        }

        state.roadNodePreDragPositions.forEach((preDragPos, roadNodeId) => {
          setNodes(prev =>
            prev.map(n =>
              n.id === `road-node-${roadNodeId}`
                ? { ...n, position: { x: preDragPos.x + delta.x, y: preDragPos.y + delta.y } }
                : n,
            ),
          )
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutObjectStore.layoutObjects, roadNetworkViewStore.nodes, setNodes],
  )

  const handleRoadNodeDragStop = useCallback(
    (node: Node): boolean => {
      if (node.type === 'roadNode') {
        const roadData = node.data as RoadNodeWithActions

        // Access-point-linked nodes don't move — discard any drag attempt.
        if (roadData.blockAccessPointId) return true

        const finalPosition = roadData.layoutObjectId
          ? clampRoadNodeToGate(node.position, roadData.layoutObjectId)
          : node.position

        roadNetworkViewStore.setNodePosition(roadData.id, finalPosition)

        return true
      }

      const isDragStopParent = isRoadConnectableLayoutObject(node) || node.type === 'yardBlock'

      if (isDragStopParent) {
        const state = dragState.current.get(node.id)

        if (state) {
          const delta = {
            x: node.position.x - state.nodePreDragPos.x,
            y: node.position.y - state.nodePreDragPos.y,
          }

          state.roadNodePreDragPositions.forEach((preDragPos, roadNodeId) => {
            roadNetworkViewStore.setNodePosition(roadNodeId, {
              x: preDragPos.x + delta.x,
              y: preDragPos.y + delta.y,
            })
          })

          dragState.current.delete(node.id)
        }

        // layout object / yardBlock position itself is handled by the caller
        return false
      }

      return false
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [layoutObjectStore.layoutObjects, roadNetworkViewStore],
  )

  return { onNodeDragStart, onNodeDrag, handleRoadNodeDragStop }
}
